/**
 * The icon set, matching the standalone tracker.
 *
 * The tracker draws these with lucide-react. Project Hub does not carry that
 * dependency and does not need to for eighteen glyphs, so these are the same
 * shapes on the same grid: a 24×24 box, 1.5px strokes, round caps and joins,
 * never filled. That combination is what makes an icon set read as one set —
 * a 2px stroke or a square cap in the middle of it looks like a mistake even
 * when you cannot say why.
 *
 * Geometry follows lucide (ISC), so a glyph here and the same glyph in the
 * tracker are the same drawing.
 *
 * Sizing convention: 18 in the sidebar and in rows, 22 in the phone's tab
 * bar, 14–16 inline beside text. Pass `size`; never scale with a transform,
 * which thins the stroke with it.
 */

type IconProps = { size?: number };

/** Every icon shares this frame — the whole reason the set looks like a set. */
function frame(size: number) {
  return {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.5,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
}

/* -- Navigation ------------------------------------------------------------- */

export function DashboardIcon({ size = 18 }: IconProps) {
  return (
    <svg {...frame(size)} aria-hidden>
      <rect x="3" y="3" width="7" height="9" rx="1" />
      <rect x="14" y="3" width="7" height="5" rx="1" />
      <rect x="14" y="12" width="7" height="9" rx="1" />
      <rect x="3" y="16" width="7" height="5" rx="1" />
    </svg>
  );
}

/** Time, and the phone's Today tab. */
export function TimeIcon({ size = 18 }: IconProps) {
  return (
    <svg {...frame(size)} aria-hidden>
      <path d="M12 6v6l4 2" />
      <circle cx="12" cy="12" r="10" />
    </svg>
  );
}

export function TodayIcon({ size = 18 }: IconProps) {
  return (
    <svg {...frame(size)} aria-hidden>
      <path d="M8 2v4" />
      <path d="M16 2v4" />
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M3 10h18" />
      <path d="m9 16 2 2 4-4" />
    </svg>
  );
}

export function UpcomingIcon({ size = 18 }: IconProps) {
  return (
    <svg {...frame(size)} aria-hidden>
      <path d="M12 2v8" />
      <path d="m4.93 10.93 1.41 1.41" />
      <path d="M2 18h2" />
      <path d="M20 18h2" />
      <path d="m19.07 10.93-1.41 1.41" />
      <path d="M22 22H2" />
      <path d="m8 6 4-4 4 4" />
      <path d="M16 18a4 4 0 0 0-8 0" />
    </svg>
  );
}

export function CalendarIcon({ size = 18 }: IconProps) {
  return (
    <svg {...frame(size)} aria-hidden>
      <path d="M8 2v4" />
      <path d="M16 2v4" />
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M3 10h18" />
      <path d="M8 14h.01" />
      <path d="M12 14h.01" />
      <path d="M16 14h.01" />
      <path d="M8 18h.01" />
      <path d="M12 18h.01" />
      <path d="M16 18h.01" />
    </svg>
  );
}

export function ClientsIcon({ size = 18 }: IconProps) {
  return (
    <svg {...frame(size)} aria-hidden>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <path d="M16 3.128a4 4 0 0 1 0 7.744" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <circle cx="9" cy="7" r="4" />
    </svg>
  );
}

/** Reports, and the phone's Insights tab. */
export function ReportsIcon({ size = 18 }: IconProps) {
  return (
    <svg {...frame(size)} aria-hidden>
      <path d="M3 3v16a2 2 0 0 0 2 2h16" />
      <path d="M18 17V9" />
      <path d="M13 17V5" />
      <path d="M8 17v-3" />
    </svg>
  );
}

/**
 * A gear, with teeth.
 *
 * Not lucide's `settings`, which is eight rounded lobes — at 18px those read
 * as petals and the icon looks like a sun. Its `cog` is worse for this: a
 * circle with radiating spokes. So this is a real gear outline, eight square
 * teeth around a hub, generated on the 24 grid rather than drawn by eye.
 */
/** A rising line over month ticks — the revenue & capacity planner. */
export function PlannerIcon({ size = 18 }: IconProps) {
  return (
    <svg {...frame(size)} aria-hidden>
      <path d="M3 3v18h18" />
      <path d="m7 15 4-4 3 3 5-6" />
    </svg>
  );
}

export function SettingsIcon({ size = 18 }: IconProps) {
  return (
    <svg {...frame(size)} aria-hidden>
      <path d="M9.4 4.86 L10.21 1.86 L13.79 1.86 L14.6 4.86 L15.21 5.11 L17.91 3.56 L20.44 6.09 L18.89 8.79 L19.14 9.4 L22.14 10.21 L22.14 13.79 L19.14 14.6 L18.89 15.21 L20.44 17.91 L17.91 20.44 L15.21 18.89 L14.6 19.14 L13.79 22.14 L10.21 22.14 L9.4 19.14 L8.79 18.89 L6.09 20.44 L3.56 17.91 L5.11 15.21 L4.86 14.6 L1.86 13.79 L1.86 10.21 L4.86 9.4 L5.11 8.79 L3.56 6.09 L6.09 3.56 L8.79 5.11 Z" />
      <circle cx="12" cy="12" r="3.4" />
    </svg>
  );
}

export function SyncIcon({ size = 18 }: IconProps) {
  return (
    <svg {...frame(size)} aria-hidden>
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M8 16H3v5" />
    </svg>
  );
}

/* -- Timing ----------------------------------------------------------------- */

/**
 * Play and stop are the two filled shapes in the set, deliberately: a hollow
 * triangle reads as "shape", a solid one reads as "go". Everything else stays
 * a stroke.
 */
export function PlayIcon({ size = 18 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M5 5a2 2 0 0 1 3.008-1.728l11.997 6.998a2 2 0 0 1 .003 3.458l-12 7A2 2 0 0 1 5 19z" />
    </svg>
  );
}

export function StopIcon({ size = 18 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <rect x="5" y="5" width="14" height="14" rx="2" />
    </svg>
  );
}

export function StopwatchIcon({ size = 18 }: IconProps) {
  return (
    <svg {...frame(size)} aria-hidden>
      <line x1="10" x2="14" y1="2" y2="2" />
      <line x1="12" x2="15" y1="14" y2="11" />
      <circle cx="12" cy="14" r="8" />
    </svg>
  );
}
